import { HttpParams } from '@angular/common/http'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'

import {
    BaseComponent,
    FabJob,
    FabPiece,
    FabShopFloor,
    FabStampStepRequest,
    FeatureType,
    ListItem,
    replaceListItemsIfChanged,
} from '../../shared'

@Component({
    selector: 'app-fab-shop-quick',
    templateUrl: './fab-shop-quick.component.html',
    styleUrls: ['./fab-shop-quick.component.css'],
    standalone: true,
    imports: [FormsModule, WjInputModule],
})
export class FabShopQuickComponent extends BaseComponent {
    jobs: FabJob[] = []
    jobOptions: ListItem<string>[] = []
    selectedJobId = ''
    private appliedJobId = ''
    pieces: FabPiece[] = []
    filterText = ''
    stampingPieceId: string | null = null
    lastCompletedName = ''

    constructor() {
        super()
        this.refresh()
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.FabShopFloorEdit])
    }

    get filteredPieces(): FabPiece[] {
        const q = (this.filterText || '').trim().toLowerCase()
        if (!q) return this.pieces
        return this.pieces.filter(
            p =>
                (p.markCode || '').toLowerCase().includes(q) ||
                (p.name || '').toLowerCase().includes(q) ||
                (p.scope || '').toLowerCase().includes(q) ||
                (p.nextStepName || '').toLowerCase().includes(q),
        )
    }

    onJobChanged() {
        if (this.selectedJobId === this.appliedJobId) return
        this.refresh(false)
    }

    refresh(showSpinner = true) {
        const requestedJobId = this.selectedJobId
        let params = new HttpParams()
        if (requestedJobId) {
            params = params.set('fabJobId', requestedJobId)
        }

        this.cancelableRequest(
            '',
            showSpinner,
            this.services.data.load(showSpinner, showSpinner, this.services.data.get<FabShopFloor>('api/FabViews/ShopFloor', params)).subscribe({
                next: result => {
                    this.initialLoadingComplete()
                    const view = result[0]
                    this.jobs = view.jobs || []
                    this.jobOptions = replaceListItemsIfChanged(this.jobOptions, [
                        new ListItem('', '(All jobs)'),
                        ...this.jobs.map(j => new ListItem(j.id, j.displayName || j.jobDescription || j.name || '')),
                    ])
                    this.appliedJobId = requestedJobId
                    if (this.selectedJobId !== requestedJobId) {
                        this.selectedJobId = requestedJobId
                    }
                    this.pieces = (view.lanes || [])
                        .flatMap(l => l.pieces || [])
                        .sort((a, b) => {
                            const ap = a.priority ?? 9999
                            const bp = b.priority ?? 9999
                            if (ap !== bp) return ap - bp
                            const ad = a.nextStepPlannedDate ? new Date(a.nextStepPlannedDate).getTime() : Number.MAX_SAFE_INTEGER
                            const bd = b.nextStepPlannedDate ? new Date(b.nextStepPlannedDate).getTime() : Number.MAX_SAFE_INTEGER
                            return ad - bd
                        })
                },
            }),
        )
    }

    stampToday(piece: FabPiece, event?: Event) {
        event?.preventDefault()
        event?.stopPropagation()
        if (!this.canEdit || !piece.nextStepId || this.stampingPieceId) return

        this.stampingPieceId = piece.id
        const body: FabStampStepRequest = {
            fabPieceId: piece.id,
            fabProcessStepId: piece.nextStepId,
            actualDate: new Date(),
            clear: false,
            note: null,
        }

        this.services.data.post('api/FabViews/StampStep', body).subscribe({
            next: () => {
                this.lastCompletedName = `${piece.markCode || piece.name} — ${piece.nextStepName}`
                this.stampingPieceId = null
                this.refresh(false)
            },
            error: () => {
                this.stampingPieceId = null
                this.services.notification.error('Failed to stamp step.')
            },
        })
    }

    formatDate(value: Date | string | null | undefined): string {
        if (!value) return '—'
        const d = new Date(value)
        if (isNaN(d.getTime())) return '—'
        return d.toISOString().slice(0, 10)
    }
}
